import { EAttachmentType, type TAttachmentType } from '@kognitos/lattice';
import { EFileType } from '../types';

/**
 * Apache Arrow/Feather2 magic bytes signature
 * "ARROW1" in ASCII: [0x41, 0x52, 0x52, 0x4F, 0x57, 0x31]
 */
export const ARROW_MAGIC_BYTES = [0x41, 0x52, 0x52, 0x4f, 0x57, 0x31] as const;

/**
 * Magic bytes signatures for file type detection
 */
const MAGIC_BYTES = {
  ARROW: ARROW_MAGIC_BYTES,
  PDF: [0x25, 0x50, 0x44, 0x46], // "%PDF"
  ZIP: [0x50, 0x4b, 0x03, 0x04], // PK.. (OOXML xlsx/docx, etc.)
  PNG: [0x89, 0x50, 0x4e, 0x47], // PNG
  JPEG: [0xff, 0xd8, 0xff], // JPEG
  GIF: [0x47, 0x49, 0x46], // GIF
  RIFF: [0x52, 0x49, 0x46, 0x46], // RIFF (WebP when followed by WEBP at offset 8)
  BMP: [0x42, 0x4d], // BM
  // OLE compound document — legacy binary Office formats (.doc, .xls, .ppt, .msg)
  OLE: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1],
} as const;

/** Max bytes to scan inside a ZIP for OOXML path hints (local headers use stored filenames). */
const OOXML_ZIP_SNIFF_BYTES = 65536;

/** Max blob size for full-text JSON sniff (avoids large binary reads). */
const JSON_TEXT_SNIFF_MAX_BYTES = 512 * 1024;

/**
 * Returns true when every signature byte matches the start of `bytes`.
 *
 * @param bytes - Bytes read from the blob head
 * @param signature - Expected magic sequence
 * @returns Whether the blob starts with this signature
 */
function matchesMagicPrefix(
  bytes: Uint8Array,
  signature: readonly number[],
): boolean {
  return (
    bytes.length >= signature.length &&
    signature.every((byte, index) => bytes[index] === byte)
  );
}

/**
 * Detects OOXML Excel vs Word from ZIP local headers (filenames are stored uncompressed).
 *
 * @param blob - Full blob (ZIP / OOXML)
 * @returns Excel or Word file type, or null if not OOXML
 */
async function detectOoxmlTypeFromZip(blob: Blob): Promise<EFileType | null> {
  const sniffSize = Math.min(blob.size, OOXML_ZIP_SNIFF_BYTES);
  if (sniffSize < MAGIC_BYTES.ZIP.length) return null;

  const slice = blob.slice(0, sniffSize);
  const buf = await slice.arrayBuffer();
  // Latin-1 maps each byte 0-255 to Unicode; safe for substring search on ZIP bytes
  const haystack = new TextDecoder('iso-8859-1').decode(buf);

  // OpenDocument (.ods/.odt/.odp) and binary OOXML (.xlsb) are zip-based but
  // can't be rendered yet — flag as unsupported so they hit the download fallback.
  if (haystack.includes('application/vnd.oasis.opendocument')) {
    return EFileType.UNSUPPORTED;
  }
  if (haystack.includes('xl/workbook.bin')) {
    return EFileType.UNSUPPORTED;
  }

  if (
    haystack.includes('xl/workbook.xml') ||
    haystack.includes('/xl/worksheets/')
  ) {
    return EFileType.EXCEL;
  }
  if (
    haystack.includes('word/document.xml') ||
    haystack.includes('/word/document.xml')
  ) {
    return EFileType.DOCX;
  }
  return null;
}

/**
 * If the blob is valid JSON (UTF-8), returns JSON file type.
 *
 * @param blob - Candidate text/json blob
 * @returns JSON type or null
 */
async function detectJsonFromUtf8Text(blob: Blob): Promise<EFileType | null> {
  if (blob.size === 0 || blob.size > JSON_TEXT_SNIFF_MAX_BYTES) return null;

  try {
    const buf = await blob.arrayBuffer();
    const text = new TextDecoder('utf-8', { fatal: false }).decode(buf);
    const trimmed = text.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
    JSON.parse(trimmed);
    return EFileType.JSON;
  } catch {
    return null;
  }
}

/**
 * Detects file type from blob content using magic bytes and light content sniffing.
 *
 * Recognizes: Arrow/Feather, PDF, PNG, JPEG, GIF, BMP, WebP, OOXML ZIP (xlsx/docx via
 * path names in local headers), and UTF-8 JSON for reasonably sized blobs.
 *
 * @param blob - The file blob to analyze
 * @returns The detected file type from magic bytes, or null if not recognized
 */
export async function detectFileTypeFromMagicBytes(
  blob: Blob,
): Promise<EFileType | null> {
  try {
    // Read first 12 bytes (WebP needs RIFF + size + WEBP)
    const headerSize = Math.min(12, blob.size);
    if (headerSize === 0) return null;

    const headerSlice = blob.slice(0, headerSize);
    const arrayBuffer = await headerSlice.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);

    // Check for Apache Arrow/Feather (ARROW1)
    if (matchesMagicPrefix(bytes, ARROW_MAGIC_BYTES)) {
      return EFileType.FEATHER;
    }

    // Check for PDF
    if (matchesMagicPrefix(bytes, MAGIC_BYTES.PDF)) {
      return EFileType.PDF;
    }

    // Check for PNG
    if (matchesMagicPrefix(bytes, MAGIC_BYTES.PNG)) {
      return EFileType.IMG;
    }

    // Check for JPEG
    if (matchesMagicPrefix(bytes, MAGIC_BYTES.JPEG)) {
      return EFileType.IMG;
    }

    // Check for GIF
    if (matchesMagicPrefix(bytes, MAGIC_BYTES.GIF)) {
      return EFileType.GIF;
    }

    // BMP (bitmap) — starts with "BM"
    if (matchesMagicPrefix(bytes, MAGIC_BYTES.BMP)) {
      return EFileType.IMG;
    }

    // OLE compound — legacy binary Office (.doc/.xls/.ppt/.msg); not renderable
    if (matchesMagicPrefix(bytes, MAGIC_BYTES.OLE)) {
      return EFileType.UNSUPPORTED;
    }

    // WebP — RIFF chunk with "WEBP" at offset 8
    if (
      bytes.length >= 12 &&
      matchesMagicPrefix(bytes, MAGIC_BYTES.RIFF) &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
    ) {
      return EFileType.IMG;
    }

    // OOXML (xlsx / docx) — ZIP with path hints in uncompressed local headers
    if (matchesMagicPrefix(bytes, MAGIC_BYTES.ZIP)) {
      const ooxml = await detectOoxmlTypeFromZip(blob);
      if (ooxml) return ooxml;
    }

    // UTF-8 JSON (common for API payloads with generic octet-stream + extensionless names)
    const jsonType = await detectJsonFromUtf8Text(blob);
    if (jsonType) return jsonType;

    return null;
  } catch {
    return null;
  }
}

/**
 * Supported audio file MIME types
 */
export const SUPPORTED_AUDIO_FILE_TYPES = [
  'audio/wav',
  'audio/mp3',
  'audio/mpeg',
  'audio/m4a',
];

/**
 * Supported video file MIME types
 */
export const SUPPORTED_VIDEO_FILE_TYPES = [
  'video/mp4',
  'video/webm',
  'video/mov',
  'video/avi',
];

/**
 * File types that need text decoding before rendering
 */
export const FILE_TYPES_THAT_NEED_DECODING: EFileType[] = [
  EFileType.CSV,
  EFileType.JSON,
  EFileType.YML,
  EFileType.TXT,
  EFileType.HTML,
  EFileType.MARKDOWN,
  EFileType.PYTHON,
];

/**
 * Extracts the file extension from a filename
 *
 * @param fileName - The filename to extract extension from
 * @returns The file extension in lowercase, or empty string if no extension found
 */
export function getFileExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/**
 * Detects file type based on filename extension
 *
 * @param fileName - The filename to analyze
 * @returns The detected file type, or null if extension is not recognized
 *
 * @remarks
 * Supports common file extensions including:
 * - Office documents: xlsx, xls, xlsb, xlsm, docx, doc
 * - Data files: feather, ftr, arrow (Apache Arrow/Feather)
 * - Images: gif, jpg, jpeg, png, webp, tiff, tif, svg
 * - Media: mp4, webm, mov, avi, wav, mp3, mpeg, m4a
 * - Text formats: json, yml, yaml, py, csv, tsv, txt, html, htm
 * - Documents: pdf, eml, msg
 */
function detectFileTypeFromExtension(fileName: string): EFileType | null {
  const extension = getFileExtension(fileName);

  switch (extension) {
    case 'xlsx':
    case 'xls':
    case 'xlsb':
    case 'xlsm':
      return EFileType.EXCEL;
    case 'feather':
    case 'ftr':
    case 'arrow':
      return EFileType.FEATHER;
    case 'gif':
      return EFileType.GIF;
    case 'mp4':
    case 'webm':
    case 'mov':
    case 'avi':
      return EFileType.VIDEO;
    case 'wav':
    case 'mp3':
    case 'mpeg':
    case 'm4a':
      return EFileType.AUDIO;
    case 'json':
      return EFileType.JSON;
    case 'yml':
    case 'yaml':
      return EFileType.YML;
    case 'py':
      return EFileType.PYTHON;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'webp':
    case 'tiff':
    case 'tif':
    case 'svg':
      return EFileType.IMG;
    case 'pdf':
      return EFileType.PDF;
    case 'csv':
    case 'tsv':
      return EFileType.CSV;
    case 'txt':
      return EFileType.TXT;
    case 'html':
    case 'htm':
      return EFileType.HTML;
    case 'md':
    case 'markdown':
      return EFileType.MARKDOWN;
    case 'docx':
    case 'doc':
      return EFileType.DOCX;
    case 'eml':
    case 'msg':
      return EFileType.EML;
    default:
      return null;
  }
}

/**
 * Detects file type based on MIME content type
 *
 * @param contentType - The MIME content type (e.g., "image/png", "application/pdf")
 * @returns The detected file type, or null if content type is not recognized
 *
 * @remarks
 * Strips parameters (like charset) from content type before detection.
 * Supports common MIME types including:
 * - Images: image/* (with special handling for GIF)
 * - Text formats: text/csv, text/plain, text/html, text/x-python
 * - Application formats: application/json, application/pdf, application/vnd.*
 * - Office documents: application/vnd.openxmlformats-officedocument.*
 * - Data files: application/vnd.apache.arrow.*, application/x-feather
 * - Email: message/rfc822
 * - Media: audio/*, video/* (from SUPPORTED_AUDIO_FILE_TYPES and SUPPORTED_VIDEO_FILE_TYPES)
 */
export function detectFileTypeFromContentType(
  contentType: string,
): EFileType | null {
  // Strip parameters like charset from content type
  const cleanContentType = contentType.split(';')[0].trim();

  // Image files
  if (cleanContentType.startsWith('image/')) {
    if (cleanContentType.endsWith('gif')) {
      return EFileType.GIF;
    }
    return EFileType.IMG;
  }

  // Text-based formats
  if (
    cleanContentType === 'text/csv' ||
    cleanContentType === 'text/tab-separated-values'
  )
    return EFileType.CSV;
  if (cleanContentType === 'text/plain') return EFileType.TXT;
  if (
    cleanContentType === 'text/x-python' ||
    cleanContentType === 'text/x-python-script'
  )
    return EFileType.PYTHON;
  if (cleanContentType.startsWith('text/html')) return EFileType.HTML;
  if (
    cleanContentType === 'text/markdown' ||
    cleanContentType === 'text/x-markdown'
  )
    return EFileType.MARKDOWN;

  // Application formats
  if (cleanContentType.includes('application/json')) return EFileType.JSON;
  if (
    (cleanContentType.startsWith('application') ||
      cleanContentType.startsWith('text')) &&
    (cleanContentType.endsWith('yml') || cleanContentType.endsWith('yaml'))
  )
    return EFileType.YML;
  if (cleanContentType === 'application/pdf') return EFileType.PDF;

  // Office documents
  if (
    [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/vnd.oasis.opendocument.spreadsheet',
    ].includes(cleanContentType)
  )
    return EFileType.EXCEL;

  // Apache Arrow/Feather files
  if (
    [
      'application/vnd.apache.arrow.file',
      'application/x-feather',
      'application/vnd.apache.arrow.stream',
    ].includes(cleanContentType)
  )
    return EFileType.FEATHER;

  if (
    [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ].includes(cleanContentType)
  )
    return EFileType.DOCX;

  // Email
  if (cleanContentType === 'message/rfc822') return EFileType.EML;

  // Media files
  if (SUPPORTED_AUDIO_FILE_TYPES.includes(cleanContentType))
    return EFileType.AUDIO;
  if (SUPPORTED_VIDEO_FILE_TYPES.includes(cleanContentType))
    return EFileType.VIDEO;

  return null;
}

/**
 * Detects file type from filename and optional content type
 *
 * Uses a two-step detection strategy:
 * 1. First tries content type (if provided and not generic)
 * 2. Falls back to file extension if content type detection fails
 *
 * @param fileName - The name of the file (required)
 * @param contentType - The MIME type / content type (optional)
 * @returns The detected file type, or EFileType.UNKNOWN if unable to determine
 *
 * @remarks
 * Generic content types (application/octet-stream, etc.) are ignored
 * in favor of file extension detection for better accuracy.
 */
export function detectFileType(
  fileName: string,
  contentType?: string,
): EFileType {
  // Try content type first if provided and it's not a generic type
  if (
    contentType &&
    ![
      'application/octet-stream',
      'application/octet',
      'binary/octet-stream',
    ].includes(contentType)
  ) {
    const typeFromContentType = detectFileTypeFromContentType(contentType);
    if (typeFromContentType) {
      return typeFromContentType;
    }
  }

  // Fall back to file extension
  const typeFromExtension = detectFileTypeFromExtension(fileName);
  if (typeFromExtension) {
    return typeFromExtension;
  }

  // Default to UNKNOWN
  return EFileType.UNKNOWN;
}

/**
 * Maps EFileType to EAttachmentType for chat attachment display.
 *
 * @param fileType - File type from file-type-detection
 * @returns Attachment type for the file
 */
export function mapFileTypeToAttachmentType(
  fileType: EFileType | null,
): TAttachmentType {
  if (!fileType) {
    return EAttachmentType.FILE;
  }

  switch (fileType) {
    case EFileType.IMG:
    case EFileType.GIF:
      return EAttachmentType.IMAGE;
    case EFileType.PDF:
      return EAttachmentType.PDF;
    case EFileType.EXCEL:
      return EAttachmentType.EXCEL;
    case EFileType.TXT:
    case EFileType.CSV:
    case EFileType.HTML:
    case EFileType.MARKDOWN:
    case EFileType.YML:
    case EFileType.PYTHON:
      return EAttachmentType.TEXT;
    case EFileType.JSON:
    default:
      return EAttachmentType.FILE;
  }
}

/**
 * Gets the attachment type for a File object.
 *
 * This is a convenience utility that combines detectFileType and mapFileTypeToAttachmentType.
 * Uses both MIME type and filename extension for robust detection.
 *
 * @param file - The File object to detect attachment type for
 * @returns The attachment type suitable for chat attachments
 *
 * @example
 * ```ts
 * const attachmentType = getAttachmentTypeFromFile(file);
 * // Returns EAttachmentType.IMAGE for image files
 * // Returns EAttachmentType.PDF for PDF files
 * // Returns EAttachmentType.FILE for unknown types
 * ```
 */
export function getAttachmentTypeFromFile(file: File): TAttachmentType {
  const fileType = detectFileType(file.name, file.type);
  return mapFileTypeToAttachmentType(fileType);
}
